import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Select,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";

function Navbar() {
  const navigate = useNavigate();
  const { lang } = useParams();
  const location = useLocation();

  const texts = {
    en: {
      home: "Home",
      pinta: "Pinta",
      privacy: "Privacy",
      contact: "Contact",
    },
    es: {
      home: "Inicio",
      pinta: "Pinta",
      privacy: "Privacidad",
      contact: "Contacto",
    },
    fr: {
      home: "Accueil",
      pinta: "Pinta",
      privacy: "Confidentialité",
      contact: "Contact",
    },
    de: {
      home: "Startseite",
      pinta: "Pinta",
      privacy: "Datenschutz",
      contact: "Kontakt",
    },
    it: {
      home: "Home",
      pinta: "Pinta",
      privacy: "Privacy",
      contact: "Contatto",
    },
  };

  const t = texts[lang] || texts.en;

  // Guarda preferencia de idioma
  useEffect(() => {
    const availableLangs = ["es", "en", "fr", "de", "it"];
    if (availableLangs.includes(lang)) {
      localStorage.setItem("preferredLang", lang);
    }
  }, [lang]);

  const handleLanguageChange = (event) => {
    const newLang = event.target.value;
    const currentPath = location.pathname;
    const segments = currentPath.split("/");
    segments[1] = newLang;
    const newPath = segments.join("/");
    navigate(newPath);
  };

  const linkStyle = {
    color: "rgba(255, 255, 255, 0.7)",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    marginRight: "20px",
    fontWeight: "normal",
  };

  const activeStyle = {
    color: "white",
    fontWeight: "bold",
  };

  // Estado para el Drawer
  const [openDrawer, setOpenDrawer] = useState(false);

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setOpenDrawer(open);
  };

  const navItems = [
    { to: `/${lang}/home`, label: t.home },
    // Pinta se ha mudado a su propio sitio: enlace externo (no React Router),
    // así la navegación dentro del SPA también sale a pintacoloring.com.
    { href: "https://www.pintacoloring.com/", label: t.pinta, external: true },
    { to: `/${lang}/privacy`, label: t.privacy },
    { to: `/${lang}/contact`, label: t.contact },
  ];

  // Menú lateral para pantallas pequeñas
  const drawerContent = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        {navItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton
              component={item.external ? "a" : NavLink}
              {...(item.external ? { href: item.href } : { to: item.to })}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem>
          <Select
            value={lang}
            onChange={handleLanguageChange}
            variant="standard"
            sx={{ fontSize: "0.875rem" }}
            // Determinamos la abreviatura que se mostrará una vez seleccionado
            renderValue={(selected) => {
              const shortMap = {
                en: "En",
                es: "Es",
                fr: "Fr",
                de: "De",
                it: "It",
              };
              return shortMap[selected] || "En"; // Por defecto "En" si no coincide
            }}
          >
            <MenuItem value="en" sx={{ fontSize: "0.875rem" }}>
              English
            </MenuItem>
            <MenuItem value="es" sx={{ fontSize: "0.875rem" }}>
              Español
            </MenuItem>
            <MenuItem value="fr" sx={{ fontSize: "0.875rem" }}>
              Français
            </MenuItem>
            <MenuItem value="de" sx={{ fontSize: "0.875rem" }}>
              Deutsch
            </MenuItem>
            <MenuItem value="it" sx={{ fontSize: "0.875rem" }}>
              Italiano
            </MenuItem>
          </Select>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="fixed">
        <Toolbar>
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <img
              src="/resources/pintative-logo-white.png"
              alt="Pintative Logo"
              style={{ maxWidth: "36px", marginRight: "3px" }}
            />
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: "regular",
                color: "white",
                lineHeight: 1,
                position: "relative",
                top: "9px",
              }}
            >
              PINTATIVE
            </Typography>
          </Box>

          {/* Enlaces en pantalla grande */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              mt: 2,
            }}
          >
            {navItems.map((item, index) =>
              item.external ? (
                <a key={index} href={item.href} style={linkStyle}>
                  {item.label}
                </a>
              ) : (
                <NavLink
                  key={index}
                  to={item.to}
                  style={({ isActive }) =>
                    isActive ? { ...linkStyle, ...activeStyle } : linkStyle
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
            <Select
              value={lang}
              onChange={handleLanguageChange}
              variant="standard"
              sx={{
                color: "inherit",
                marginLeft: "20px",
                fontSize: "0.875rem",
              }}
              renderValue={(selected) => {
                const shortMap = {
                  en: "En",
                  es: "Es",
                  fr: "Fr",
                  de: "De",
                  it: "It",
                };
                // Devuelve la abreviatura o "En" por defecto si no coincide
                return shortMap[selected] || "En";
              }}
            >
              <MenuItem value="en" sx={{ fontSize: "0.875rem" }}>
                English
              </MenuItem>
              <MenuItem value="es" sx={{ fontSize: "0.875rem" }}>
                Español
              </MenuItem>
              <MenuItem value="fr" sx={{ fontSize: "0.875rem" }}>
                Français
              </MenuItem>
              <MenuItem value="de" sx={{ fontSize: "0.875rem" }}>
                Deutsch
              </MenuItem>
              <MenuItem value="it" sx={{ fontSize: "0.875rem" }}>
                Italiano
              </MenuItem>
            </Select>
          </Box>

          {/* Ícono menú hamburguesa en pantallas pequeñas */}
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ display: { xs: "inline-flex", md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Drawer para menú móvil */}
      <Drawer anchor="right" open={openDrawer} onClose={toggleDrawer(false)}>
        {drawerContent}
      </Drawer>
    </>
  );
}

export default Navbar;
